// Tutorial4.jsx (Updated with Diagrams)
import React from 'react';
import Navbar from '../../Navbar';
import Sidebar from '../../Sidebar';
import { BlockMath } from 'react-katex';
import '../../../styles/Tutorial4.css';

const ChemicalReactionEngineeringNotes4 = () => {
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>

      <Sidebar />

      <div style={{
        marginLeft:    '220px',
        width:         'calc(100% - 220px)',
        display:       'flex',
        flexDirection: 'column',
        height:        '100vh',
        overflow:      'hidden',
      }}>

        <Navbar activePage="app" />

        <div style={{
          flex:      1,
          marginTop: '68px',
          overflowY: 'auto',
          overflowX:  'hidden',
          minHeight: 0,
        }}>
    <div className="cre4-container" style={{ background: 'var(--t4-bg)', minHeight: '100%' }}>

      {/* SIDEBAR */}
      <nav className="cre4-sidebar">
        <div className="cre4-sidebar-header">
          <h1>CRE Course</h1>
          <p>Prof. Fogler · Lecture 4</p>
        </div>

        <div className="cre4-nav-section">Building Block 1 · Mole Balances</div>
        <a className="cre4-nav-item" href="#mole-balance-review">Mole Balances Review (X)</a>
        <a className="cre4-nav-item cre4-nav-item--sub" href="#batch-x">↳ Batch in terms of X</a>
        <a className="cre4-nav-item cre4-nav-item--sub" href="#cstr-x">↳ CSTR in terms of X</a>
        <a className="cre4-nav-item cre4-nav-item--sub" href="#pfr-x">↳ PFR in terms of X</a>
        <a className="cre4-nav-item cre4-nav-item--sub" href="#pbr-x">↳ PBR in terms of X</a>

        <div className="cre4-nav-section">Building Block 2 · Rate Laws</div>
        <a className="cre4-nav-item" href="#power-law">Power Law Model</a>
        <a className="cre4-nav-item" href="#elementary-rate">Elementary Rate Law</a>
        <a className="cre4-nav-item" href="#arrhenius">Arrhenius Equation</a>

        <div className="cre4-nav-section">Building Block 3 · Stoichiometry</div>
        <a className="cre4-nav-item" href="#stoichiometry-table">Stoichiometry Table</a>
        <a className="cre4-nav-item cre4-nav-item--sub" href="#batch-stoich">↳ Batch System</a>
        <a className="cre4-nav-item cre4-nav-item--sub" href="#constant-volume">↳ Constant Volume Batch</a>
        <a className="cre4-nav-item cre4-nav-item--sub" href="#flow-stoich">↳ Flow System</a>

        <div className="cre4-nav-section">Equilibrium Conversion</div>
        <a className="cre4-nav-item" href="#batch-equilibrium">Batch Reactor Example</a>
        <a className="cre4-nav-item" href="#algorithm">CRE Algorithm</a>

        <div className="cre4-nav-section">Reference</div>
        <a className="cre4-nav-item" href="#key-formulas">Key Formulas</a>
        <a className="cre4-nav-item" href="#key-symbols">Key Symbols</a>
      </nav>

      {/* MAIN CONTENT */}
      <main className="cre4-main">
        <div className="cre4-main-inner">

          {/* HERO */}
          <div className="cre4-hero">
            <div className="cre4-hero-tag">Open Course Notes</div>
            <h1>CRE — Lecture 4: Rate Laws, Stoichiometry &amp; Equilibrium</h1>
            <p className="cre4-hero-desc">
              This lecture completes the three building blocks of Chemical Reaction Engineering: 
              <strong>Mole Balances</strong> (conversion review), <strong>Rate Laws</strong> (power law models, Arrhenius equation),
              and <strong>Stoichiometry</strong> (stoichiometric tables, concentration expressions). 
              Finally, we calculate <strong>equilibrium conversion Xₑ</strong> for batch and flow reactors.
            </p>
            <div className="cre4-hero-meta">
              <span className="cre4-meta-item"><span className="cre4-meta-dot"></span>Fogler's Elements of CRE, 6th Ed.</span>
              <span className="cre4-meta-item"><span className="cre4-meta-dot"></span>Lecture 4 · Chapters 2-4</span>
              <span className="cre4-meta-item"><span className="cre4-meta-dot"></span>University of Michigan</span>
            </div>
          </div>

          {/* TOC */}
          <div className="cre4-toc-box">
            <h3>Quick Navigation</h3>
            <div className="cre4-toc-grid">
              <a className="cre4-toc-link" href="#mole-balance-review">Mole Balances (X)</a>
              <a className="cre4-toc-link" href="#power-law">Power Law Rate Law</a>
              <a className="cre4-toc-link" href="#arrhenius">Arrhenius Equation</a>
              <a className="cre4-toc-link" href="#stoichiometry-table">Stoichiometry Table</a>
              <a className="cre4-toc-link" href="#batch-stoich">Batch Stoichiometry</a>
              <a className="cre4-toc-link" href="#constant-volume">Constant Volume Batch</a>
              <a className="cre4-toc-link" href="#flow-stoich">Flow Stoichiometry</a>
              <a className="cre4-toc-link" href="#batch-equilibrium">Equilibrium Conversion</a>
              <a className="cre4-toc-link" href="#algorithm">CRE Algorithm</a>
              <a className="cre4-toc-link" href="#key-formulas">Key Formulas</a>
            </div>
          </div>

          {/* ══════════════════════════════════════════════
              DIAGRAM 1: Two Steps to get -r_A = f(X)
          ══════════════════════════════════════════════ */}
          <section className="cre4-section" id="two-steps-diagram">
            <div className="cre4-section-label">Visual Summary</div>
            <h2>Two Steps to Get −r<sub>A</sub> = f(X)</h2>
            <div className="cre4-section-divider"></div>
            
            <div className="cre4-two-steps-container">
              <div className="cre4-two-steps-diagram">
                <div className="cre4-step-box cre4-step-box--rate">
                  <div className="cre4-step-box-title">Step 1: Rate Law</div>
                  <div className="cre4-step-box-eq">
                    <BlockMath math="-r_A = g(C_i)" />
                  </div>
                  <div className="cre4-step-box-desc">Rate of reaction as function of concentrations</div>
                </div>
                
                <div className="cre4-step-arrow">→</div>
                
                <div className="cre4-step-box cre4-step-box--stoich">
                  <div className="cre4-step-box-title">Step 2: Stoichiometry</div>
                  <div className="cre4-step-box-eq">
                    <BlockMath math="C_i = h(X)" />
                  </div>
                  <div className="cre4-step-box-desc">Concentrations as function of conversion</div>
                </div>
                
                <div className="cre4-step-arrow">→</div>
                
                <div className="cre4-step-box cre4-step-box--combine">
                  <div className="cre4-step-box-title">Step 3: Combine</div>
                  <div className="cre4-step-box-eq">
                    <BlockMath math="-r_A = f(X)" />
                  </div>
                  <div className="cre4-step-box-desc">Rate as function of conversion only</div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════
              BLOCK 1 · MOLE BALANCES (Review in terms of X)
          ══════════════════════════════════════════════ */}
          <section className="cre4-section" id="mole-balance-review">
            <div className="cre4-lec-badge cre4-lec-badge--block1">Building Block 1 · Mole Balances</div>
            <div className="cre4-section-label">Review</div>
            <h2>Reactor Mole Balances in Terms of Conversion, X</h2>
            <div className="cre4-section-divider"></div>
            <p>
              Conversion X is defined as the fraction of the limiting reactant A that has been consumed.
              All reactor design equations can be rewritten in terms of X, making it the central design variable.
            </p>

            {/* DIAGRAM 2: Reactor types visual summary */}
            <div className="cre4-reactors-diagram">
              <div className="cre4-reactor-icon cre4-reactor-icon--batch">
                <svg viewBox="0 0 100 80" width="80" height="64">
                  <rect x="10" y="10" width="80" height="60" rx="5" fill="rgba(212,160,23,0.15)" stroke="#d4a017" strokeWidth="1.5"/>
                  <text x="50" y="45" textAnchor="middle" fontSize="9" fill="#d4a017" fontFamily="monospace">Batch</text>
                </svg>
              </div>
              <div className="cre4-reactor-icon cre4-reactor-icon--cstr">
                <svg viewBox="0 0 100 80" width="80" height="64">
                  <rect x="10" y="10" width="80" height="60" rx="5" fill="rgba(196,92,44,0.15)" stroke="#c45c2c" strokeWidth="1.5"/>
                  <text x="50" y="45" textAnchor="middle" fontSize="9" fill="#c45c2c" fontFamily="monospace">CSTR</text>
                </svg>
              </div>
              <div className="cre4-reactor-icon cre4-reactor-icon--pfr">
                <svg viewBox="0 0 100 80" width="80" height="64">
                  <rect x="10" y="10" width="80" height="60" rx="30" fill="rgba(58,107,75,0.15)" stroke="#3a6b4b" strokeWidth="1.5"/>
                  <text x="50" y="45" textAnchor="middle" fontSize="9" fill="#3a6b4b" fontFamily="monospace">PFR</text>
                </svg>
              </div>
            </div>

            <table className="cre4-table">
              <thead>
                <tr>
                  <th>Reactor</th>
                  <th>Differential Form</th>
                  <th>Algebraic Form</th>
                  <th>Integral Form</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="cre4-badge cre4-badge--batch">Batch</span></td>
                  <td><BlockMath math="N_{A0}\frac{dX}{dt} = -r_A V" /></td>
                  <td>—</td>
                  <td><BlockMath math="t = N_{A0}\int_0^X \frac{dX}{-r_A V}" /></td>
                </tr>
                <tr>
                  <td><span className="cre4-badge cre4-badge--cstr">CSTR</span></td>
                  <td>—</td>
                  <td><BlockMath math="V = \frac{F_{A0}X}{-r_A}" /></td>
                  <td>—</td>
                </tr>
                <tr>
                  <td><span className="cre4-badge cre4-badge--pfr">PFR</span></td>
                  <td><BlockMath math="F_{A0}\frac{dX}{dV} = -r_A" /></td>
                  <td>—</td>
                  <td><BlockMath math="V = F_{A0}\int_0^X \frac{dX}{-r_A}" /></td>
                </tr>
                <tr>
                  <td><span className="cre4-badge cre4-badge--pbr">PBR</span></td>
                  <td><BlockMath math="F_{A0}\frac{dX}{dW} = -r_A^{\prime}" /></td>
                  <td>—</td>
                  <td><BlockMath math="W = F_{A0}\int_0^X \frac{dX}{-r_A^{\prime}}" /></td>
                </tr>
              </tbody>
            </table>

            <div className="cre4-callout cre4-callout--info">
              <p><strong>Key Point:</strong> The PFR and PBR equations integrate 1/(-r_A) over X. The CSTR equation is algebraic — (-r_A) is evaluated at the exit conversion. The batch equation integrates over time to achieve a desired X.</p>
            </div>
          </section>

          {/* Batch */}
          <section className="cre4-section" id="batch-x">
            <div className="cre4-lec-badge cre4-lec-badge--block1">Building Block 1 · Mole Balances</div>
            <div className="cre4-section-label">Batch Reactor</div>
            <h2>Batch Design Equation in Terms of X</h2>
            <div className="cre4-section-divider"></div>
            <div className="cre4-reactor-block cre4-reactor-block--batch">
              <div className="cre4-reactor-block-header">Batch — Moles of A Remaining</div>
              <div className="cre4-reactor-block-body">
                <div className="cre4-eq-box">
                  <div className="cre4-eq-label">Material Balance on A</div>
                  <div className="cre4-eq-main">
                    <BlockMath math="N_A = N_{A0} - N_{A0}X" />
                    <BlockMath math="dN_A = -N_{A0}\,dX" />
                  </div>
                </div>
                <div className="cre4-eq-box">
                  <div className="cre4-eq-label">Differential Form</div>
                  <div className="cre4-eq-main"><BlockMath math="N_{A0}\frac{dX}{dt} = -r_A V" /></div>
                </div>
                <div className="cre4-eq-box">
                  <div className="cre4-eq-label">Integral Form (Time to achieve X)</div>
                  <div className="cre4-eq-main"><BlockMath math="t = N_{A0}\int_0^X \frac{dX}{-r_A V}" /></div>
                </div>
              </div>
            </div>
          </section>

          {/* CSTR */}
          <section className="cre4-section" id="cstr-x">
            <div className="cre4-lec-badge cre4-lec-badge--block1">Building Block 1 · Mole Balances</div>
            <div className="cre4-section-label">CSTR</div>
            <h2>CSTR Design Equation in Terms of X</h2>
            <div className="cre4-section-divider"></div>
            <div className="cre4-reactor-block cre4-reactor-block--cstr">
              <div className="cre4-reactor-block-header">CSTR — Molar Flow Rate of A</div>
              <div className="cre4-reactor-block-body">
                <div className="cre4-eq-box">
                  <div className="cre4-eq-label">Conversion-Flow Rate Relationship</div>
                  <div className="cre4-eq-main"><BlockMath math="F_A = F_{A0} - F_{A0}X" /></div>
                </div>
                <div className="cre4-eq-box">
                  <div className="cre4-eq-label">CSTR Volume (Algebraic)</div>
                  <div className="cre4-eq-main"><BlockMath math="V = \frac{F_{A0}X}{-r_A}" /></div>
                  <div className="cre4-eq-note">Note: -r_A is evaluated at the exit conversion X (well-mixed assumption).</div>
                </div>
              </div>
            </div>
          </section>

          {/* PFR */}
          <section className="cre4-section" id="pfr-x">
            <div className="cre4-lec-badge cre4-lec-badge--block1">Building Block 1 · Mole Balances</div>
            <div className="cre4-section-label">PFR</div>
            <h2>PFR Design Equation in Terms of X</h2>
            <div className="cre4-section-divider"></div>
            <div className="cre4-reactor-block cre4-reactor-block--pfr">
              <div className="cre4-reactor-block-header">PFR — Differential and Integral Forms</div>
              <div className="cre4-reactor-block-body">
                <div className="cre4-eq-box">
                  <div className="cre4-eq-label">Differential Form</div>
                  <div className="cre4-eq-main"><BlockMath math="F_{A0}\frac{dX}{dV} = -r_A" /></div>
                </div>
                <div className="cre4-eq-box">
                  <div className="cre4-eq-label">Integral Form (Volume to achieve X)</div>
                  <div className="cre4-eq-main"><BlockMath math="V = F_{A0}\int_0^X \frac{dX}{-r_A}" /></div>
                </div>
              </div>
            </div>
          </section>

          {/* PBR */}
          <section className="cre4-section" id="pbr-x">
            <div className="cre4-lec-badge cre4-lec-badge--block1">Building Block 1 · Mole Balances</div>
            <div className="cre4-section-label">PBR</div>
            <h2>PBR Design Equation in Terms of X</h2>
            <div className="cre4-section-divider"></div>
            <div className="cre4-reactor-block cre4-reactor-block--pbr">
              <div className="cre4-reactor-block-header">PBR — Differential and Integral Forms</div>
              <div className="cre4-reactor-block-body">
                <div className="cre4-eq-box">
                  <div className="cre4-eq-label">Differential Form</div>
                  <div className="cre4-eq-main"><BlockMath math="F_{A0}\frac{dX}{dW} = -r_A^{\prime}" /></div>
                </div>
                <div className="cre4-eq-box">
                  <div className="cre4-eq-label">Integral Form (Catalyst weight to achieve X)</div>
                  <div className="cre4-eq-main"><BlockMath math="W = F_{A0}\int_0^X \frac{dX}{-r_A^{\prime}}" /></div>
                </div>
              </div>
            </div>
          </section>
                   {/* ══════════════════════════════════════════════
              REACTORS IN SERIES DIAGRAM (from Lecture 2 Review)
          ══════════════════════════════════════════════ */}
          <section className="cre4-section" id="reactors-in-series">
            <div className="cre4-lec-badge cre4-lec-badge--review">Review · Lecture 2</div>
            <div className="cre4-section-label">Reactors in Series</div>
            <h2>Reactors in Series</h2>
            <div className="cre4-section-divider"></div>
            <p>
              Given −r<sub>A</sub> as a function of conversion, we can design any <strong>sequence of reactors in series</strong> 
              by defining a <em>cumulative conversion X<sub>i</sub></em> at each point:
            </p>
            
            <div className="cre4-eq-box">
              <div className="cre4-eq-label">Cumulative Conversion at Point i</div>
              <div className="cre4-eq-main">
                <BlockMath math="X_i = \frac{\text{moles of A reacted up to point } i}{\text{moles of A fed to first reactor}}" />
              </div>
              <div className="cre4-eq-note">⚠️ Only valid if there are NO side streams between reactors.</div>
            </div>

            {/* Reactors in Series Diagram */}
            <div className="cre4-series-diagram-container">
              <div className="cre4-series-title">Reactors in Series Configuration</div>
              
              <div className="cre4-series-diagram">
                {/* Reactor 1 - CSTR */}
                <div className="cre4-series-reactor cre4-series-reactor--cstr">
                  <div className="cre4-series-reactor-label">CSTR</div>
                  <div className="cre4-series-reactor-vol">V₁</div>
                  <div className="cre4-series-reactor-x">X₁ = 0.2</div>
                </div>
                
                <div className="cre4-series-arrow">→</div>
                
                {/* Reactor 2 - PFR */}
                <div className="cre4-series-reactor cre4-series-reactor--pfr">
                  <div className="cre4-series-reactor-label">PFR</div>
                  <div className="cre4-series-reactor-vol">V₂</div>
                  <div className="cre4-series-reactor-x">X₂ = 0.6</div>
                </div>
                
                <div className="cre4-series-arrow">→</div>
                
                {/* Reactor 3 - CSTR */}
                <div className="cre4-series-reactor cre4-series-reactor--cstr">
                  <div className="cre4-series-reactor-label">CSTR</div>
                  <div className="cre4-series-reactor-vol">V₃</div>
                  <div className="cre4-series-reactor-x">X₃ = 0.65</div>
                </div>
              </div>
              
              <div className="cre4-series-note">
                <span className="cre4-series-warning">⚠️ Only valid if there are no side streams</span>
              </div>
            </div>

            <div className="cre4-callout cre4-callout--info">
              <p><strong>Series Configuration:</strong> The cumulative conversion X<sub>i</sub> increases through each reactor. 
              The design equations for each reactor use the difference in conversion:</p>
              <ul style={{marginTop: '10px', marginLeft: '20px'}}>
                <li><strong>Reactor 1 (CSTR):</strong> V₁ = F<sub>A0</sub>·(X₁ - 0)/(-r<sub>A1</sub>)</li>
                <li><strong>Reactor 2 (PFR):</strong> V₂ = F<sub>A0</sub>·∫<sub>X₁</sub><sup>X₂</sup> dX/(-r<sub>A</sub>)</li>
                <li><strong>Reactor 3 (CSTR):</strong> V₃ = F<sub>A0</sub>·(X₃ - X₂)/(-r<sub>A3</sub>)</li>
              </ul>
            </div>
          </section>
          {/* ══════════════════════════════════════════════
              BLOCK 2 · RATE LAWS
          ══════════════════════════════════════════════ */}
          <section className="cre4-section" id="power-law">
            <div className="cre4-lec-badge cre4-lec-badge--block2">Building Block 2 · Rate Laws</div>
            <div className="cre4-section-label">Power Law Model</div>
            <h2>Power Law Rate Laws</h2>
            <div className="cre4-section-divider"></div>
            <p>
              The rate law expresses the rate of reaction as a function of concentration. 
              The most common form is the <strong>power law model</strong>:
            </p>

            <div className="cre4-eq-box">
              <div className="cre4-eq-label">General Power Law Model</div>
              <div className="cre4-eq-main">
                <BlockMath math="-r_A = k\,C_A^{\alpha}C_B^{\beta}" />
              </div>
              <div className="cre4-eq-note">
                α = order of reaction with respect to A<br/>
                β = order of reaction with respect to B<br/>
                Overall reaction order = α + β
              </div>
            </div>

            <div className="cre4-callout cre4-callout--key">
              <p><strong>Elementary Rate Law:</strong> If a reaction follows an elementary rate law, the reaction orders (α, β) equal the stoichiometric coefficients of the reaction as written.</p>
            </div>

            <div className="cre4-eq-box">
              <div className="cre4-eq-label">Example: 2A + B → 3C</div>
              <div className="cre4-eq-main">
                <BlockMath math="-r_A = k_A C_A^2 C_B" />
              </div>
              <div className="cre4-eq-note">2nd order in A, 1st order in B, overall 3rd order</div>
            </div>
          </section>

          {/* Arrhenius Equation with Diagram */}
          <section className="cre4-section" id="arrhenius">
            <div className="cre4-lec-badge cre4-lec-badge--block2">Building Block 2 · Rate Laws</div>
            <div className="cre4-section-label">Temperature Dependence</div>
            <h2>Arrhenius Equation</h2>
            <div className="cre4-section-divider"></div>
            <p>
              The rate constant k depends strongly on temperature according to the <strong>Arrhenius equation</strong>:
            </p>

            <div className="cre4-eq-box">
              <div className="cre4-eq-label">Arrhenius Equation</div>
              <div className="cre4-eq-main">
                <BlockMath math="k = A e^{-E/RT}" />
              </div>
              <div className="cre4-eq-note">
                E = Activation energy (cal/mol or J/mol)<br/>
                R = Gas constant (1.987 cal/mol·K or 8.314 J/mol·K)<br/>
                T = Absolute temperature (K)<br/>
                A = Frequency factor (same units as k)
              </div>
            </div>

            {/* DIAGRAM 3: Arrhenius Plot */}
            <div className="cre4-arrhenius-container">
              <div className="cre4-arrhenius-title">Arrhenius Plot — ln(k) vs 1/T</div>
              <svg viewBox="0 0 500 300" className="cre4-arrhenius-svg" aria-label="Arrhenius plot showing ln(k) vs 1/T">
                {/* Axes */}
                <line x1="60" y1="260" x2="450" y2="260" stroke="#1a2e1a" strokeWidth="2"/>
                <line x1="60" y1="260" x2="60" y2="30" stroke="#1a2e1a" strokeWidth="2"/>
                <polygon points="450,257 450,263 458,260" fill="#1a2e1a"/>
                <polygon points="57,30 63,30 60,22" fill="#1a2e1a"/>
                
                {/* Axis labels */}
                <text x="458" y="264" fontSize="12" fill="#1a2e1a" fontFamily="monospace">1/T (K⁻¹)</text>
                <text x="20" y="150" fontSize="12" fill="#1a2e1a" fontFamily="monospace" transform="rotate(-90,20,150)" textAnchor="middle">ln(k)</text>
                
                {/* Line with negative slope */}
                <line x1="80" y1="240" x2="400" y2="60" stroke="#3a6b4b" strokeWidth="2.5"/>
                
                {/* Slope annotation */}
                <text x="300" y="180" fontSize="11" fill="#3a6b4b" fontFamily="monospace">slope = -E/R</text>
                
                {/* Tick marks */}
                <line x1="60" y1="260" x2="60" y2="266" stroke="#1a2e1a" strokeWidth="1.5"/>
                <text x="60" y="278" fontSize="10" fill="#5a6e58" textAnchor="middle" fontFamily="monospace">0</text>
              </svg>
              <div className="cre4-arrhenius-caption">
                The Arrhenius equation shows that ln(k) is linear with 1/T. The slope is -E/R, where E is the activation energy.
              </div>
            </div>

            <div className="cre4-card-grid">
              <div className="cre4-card">
                <div className="cre4-card-icon">⚡</div>
                <h4>Activation Energy E</h4>
                <p>Minimum energy required for a reaction to occur. Higher E means stronger temperature sensitivity.</p>
              </div>
              <div className="cre4-card">
                <div className="cre4-card-icon">📈</div>
                <h4>Frequency Factor A</h4>
                <p>Represents the frequency of molecular collisions with proper orientation.</p>
              </div>
              <div className="cre4-card">
                <div className="cre4-card-icon">🌡️</div>
                <h4>Temperature Effect</h4>
                <p>Small temperature increases can dramatically increase k, especially for high E reactions.</p>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════
              DIAGRAM 4: CRE Algorithm Visual (Tower)
          ══════════════════════════════════════════════ */}
          <section className="cre4-section" id="algorithm">
            <div className="cre4-section-label">Reference</div>
            <h2>The CRE Algorithm — Three Building Blocks</h2>
            <div className="cre4-section-divider"></div>
            <p>
              All CRE problems are solved by following this systematic algorithm. 
              The three building blocks must be completed in order:
            </p>

            {/* DIAGRAM 5: CRE Algorithm Tower Visual */}
            <div className="cre4-algorithm-tower">
              <div className="cre4-tower-block cre4-tower-block--1">
                <div className="cre4-tower-step">1</div>
                <div className="cre4-tower-content">
                  <h4>Mole Balance</h4>
                  <p>Write design equation in terms of X</p>
                  <div className="cre4-tower-equation"><BlockMath math="V = F_{A0}\int_0^X \frac{dX}{-r_A}" /></div>
                </div>
              </div>
              <div className="cre4-tower-arrow">↓</div>
              <div className="cre4-tower-block cre4-tower-block--2">
                <div className="cre4-tower-step">2</div>
                <div className="cre4-tower-content">
                  <h4>Rate Law</h4>
                  <p>Express -r_A as function of C_i</p>
                  <div className="cre4-tower-equation"><BlockMath math="-r_A = k\,C_A^{\alpha}C_B^{\beta}" /></div>
                </div>
              </div>
              <div className="cre4-tower-arrow">↓</div>
              <div className="cre4-tower-block cre4-tower-block--3">
                <div className="cre4-tower-step">3</div>
                <div className="cre4-tower-content">
                  <h4>Stoichiometry</h4>
                  <p>Express C_i as function of X</p>
                  <div className="cre4-tower-equation"><BlockMath math="C_A = C_{A0}(1-X)" /></div>
                </div>
              </div>
              <div className="cre4-tower-arrow">↓</div>
              <div className="cre4-tower-block cre4-tower-block--4">
                <div className="cre4-tower-step">4</div>
                <div className="cre4-tower-content">
                  <h4>Combine &amp; Solve</h4>
                  <p>Get -r_A = f(X) and integrate</p>
                  <div className="cre4-tower-equation"><BlockMath math="-r_A = f(X)" /></div>
                </div>
              </div>
            </div>

            <div className="cre4-callout cre4-callout--warn">
              <p><strong>Critical Warning:</strong> Do NOT skip the stoichiometry step! Many students try to jump directly from rate law to mole balance. This will almost always lead to incorrect reactor sizing.</p>
            </div>
          </section>

          {/* ══════════════════════════════════════════════
              BLOCK 3 · STOICHIOMETRY
          ══════════════════════════════════════════════ */}
          <section className="cre4-section" id="stoichiometry-table">
            <div className="cre4-lec-badge cre4-lec-badge--block3">Building Block 3 · Stoichiometry</div>
            <div className="cre4-section-label">Stoichiometric Tables</div>
            <h2>Stoichiometry Table Setup</h2>
            <div className="cre4-section-divider"></div>
            <p>
              For the general reaction:
            </p>
            <div className="cre4-eq-box">
              <div className="cre4-eq-main">
                <BlockMath math="\mathrm{A} + \frac{b}{a}\mathrm{B} \rightarrow \frac{c}{a}\mathrm{C} + \frac{d}{a}\mathrm{D}" />
              </div>
              <div className="cre4-eq-note">A is the limiting reactant.</div>
            </div>

            <p>
              Using stoichiometric tables, we express the number of moles (batch) or molar flow rates (flow) 
              of each species as a function of conversion X. Let:
            </p>
            <div className="cre4-eq-box">
              <div className="cre4-eq-main">
                <BlockMath math="\Theta_i = \frac{N_{i0}}{N_{A0}} = \frac{C_{i0}}{C_{A0}} = \frac{y_{i0}}{y_{A0}}" />
                <BlockMath math="\delta = \frac{d}{a} + \frac{c}{a} - \frac{b}{a} - 1" />
              </div>
              <div className="cre4-eq-note">δ = change in total number of moles per mole of A reacted</div>
            </div>

            <div className="cre4-callout cre4-callout--tip">
              <p><strong>Tip:</strong> The stoichiometric table is the bridge between the rate law (function of concentrations) and the mole balance (function of conversion). This is the most critical step for obtaining -r_A = f(X).</p>
            </div>
          </section>

          {/* Batch Stoichiometry */}
          <section className="cre4-section" id="batch-stoich">
            <div className="cre4-lec-badge cre4-lec-badge--block3">Building Block 3 · Stoichiometry</div>
            <div className="cre4-section-label">Batch System</div>
            <h2>Batch Stoichiometry Table</h2>
            <div className="cre4-section-divider"></div>

            {/* DIAGRAM 6: Batch Stoichiometry Table Visual */}
            <div className="cre4-stoich-table-visual">
              <div className="cre4-stoich-row cre4-stoich-header">
                <span>Species</span>
                <span>Initial</span>
                <span>Change</span>
                <span>Remaining</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">A</span>
                <span>N<sub>A0</sub></span>
                <span>-N<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">N<sub>A</sub> = N<sub>A0</sub>(1-X)</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">B</span>
                <span>N<sub>A0</sub>Θ<sub>B</sub></span>
                <span>-(b/a)N<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">N<sub>B</sub> = N<sub>A0</sub>(Θ<sub>B</sub> - (b/a)X)</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">C</span>
                <span>N<sub>A0</sub>Θ<sub>C</sub></span>
                <span>+(c/a)N<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">N<sub>C</sub> = N<sub>A0</sub>(Θ<sub>C</sub> + (c/a)X)</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">D</span>
                <span>N<sub>A0</sub>Θ<sub>D</sub></span>
                <span>+(d/a)N<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">N<sub>D</sub> = N<sub>A0</sub>(Θ<sub>D</sub> + (d/a)X)</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">Inert (I)</span>
                <span>N<sub>A0</sub>Θ<sub>I</sub></span>
                <span>—</span>
                <span className="cre4-stoich-result">N<sub>I</sub> = N<sub>A0</sub>Θ<sub>I</sub></span>
              </div>
              <div className="cre4-stoich-row cre4-stoich-total">
                <span>Total</span>
                <span>N<sub>T0</sub></span>
                <span>δ N<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">N<sub>T</sub> = N<sub>T0</sub> + δ N<sub>A0</sub>X</span>
              </div>
            </div>
          </section>

          {/* Constant Volume Batch */}
          <section className="cre4-section" id="constant-volume">
            <div className="cre4-lec-badge cre4-lec-badge--block3">Building Block 3 · Stoichiometry</div>
            <div className="cre4-section-label">Constant Volume Batch</div>
            <h2>Concentrations in Constant Volume Batch Reactors</h2>
            <div className="cre4-section-divider"></div>
            <p>
              For liquid phase reactions or gas phase reactions in rigid containers, volume is constant: <strong>V = V₀</strong>.
              Concentrations are then:
            </p>

            <div className="cre4-eq-box">
              <div className="cre4-eq-label">Concentrations (V = V₀)</div>
              <div className="cre4-eq-main">
                <BlockMath math="C_A = \frac{N_A}{V} = \frac{N_{A0}(1-X)}{V_0} = C_{A0}(1-X)" />
                <BlockMath math="C_B = \frac{N_B}{V} = \frac{N_{A0}}{V_0}\left(\Theta_B - \frac{b}{a}X\right) = C_{A0}\left(\Theta_B - \frac{b}{a}X\right)" />
                <BlockMath math="C_C = C_{A0}\left(\Theta_C + \frac{c}{a}X\right)" />
                <BlockMath math="C_D = C_{A0}\left(\Theta_D + \frac{d}{a}X\right)" />
              </div>
            </div>

            <div className="cre4-eq-box">
              <div className="cre4-eq-label">Example: Combine Rate Law + Stoichiometry</div>
              <div className="cre4-eq-main">
                <BlockMath math="-r_A = k_A C_A^2 C_B" />
                <BlockMath math="-r_A = k_A C_{A0}^3 (1-X)^2 \left(\Theta_B - \frac{b}{a}X\right)" />
              </div>
              <div className="cre4-eq-note">
                For equimolar feed: Θ_B = 1<br/>
                For stoichiometric feed: Θ_B = b/a
              </div>
            </div>
          </section>

          {/* Flow Stoichiometry */}
          <section className="cre4-section" id="flow-stoich">
            <div className="cre4-lec-badge cre4-lec-badge--block3">Building Block 3 · Stoichiometry</div>
            <div className="cre4-section-label">Flow System</div>
            <h2>Flow System Stoichiometry Table</h2>
            <div className="cre4-section-divider"></div>

            {/* DIAGRAM 7: Flow Stoichiometry Table Visual */}
            <div className="cre4-stoich-table-visual cre4-stoich-table-flow">
              <div className="cre4-stoich-row cre4-stoich-header">
                <span>Species</span>
                <span>Feed</span>
                <span>Change</span>
                <span>Effluent</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">A</span>
                <span>F<sub>A0</sub></span>
                <span>-F<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">F<sub>A</sub> = F<sub>A0</sub>(1-X)</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">B</span>
                <span>F<sub>A0</sub>Θ<sub>B</sub></span>
                <span>-(b/a)F<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">F<sub>B</sub> = F<sub>A0</sub>(Θ<sub>B</sub> - (b/a)X)</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">C</span>
                <span>F<sub>A0</sub>Θ<sub>C</sub></span>
                <span>+(c/a)F<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">F<sub>C</sub> = F<sub>A0</sub>(Θ<sub>C</sub> + (c/a)X)</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">D</span>
                <span>F<sub>A0</sub>Θ<sub>D</sub></span>
                <span>+(d/a)F<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">F<sub>D</sub> = F<sub>A0</sub>(Θ<sub>D</sub> + (d/a)X)</span>
              </div>
              <div className="cre4-stoich-row cre4-stoich-total">
                <span>Total</span>
                <span>F<sub>T0</sub></span>
                <span>δ F<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">F<sub>T</sub> = F<sub>T0</sub> + δ F<sub>A0</sub>X</span>
              </div>
            </div>

            <div className="cre4-callout cre4-callout--warn">
              <p><strong>Important:</strong> For liquid phase flow systems, υ = υ₀ (constant density), so concentrations are the same as batch constant volume. For gas phase systems, υ changes with conversion due to mole changes — we'll cover that in Lecture 5.</p>
            </div>

            <div className="cre4-eq-box">
              <div className="cre4-eq-label">Liquid Phase Flow System (υ = υ₀)</div>
              <div className="cre4-eq-main">
                <BlockMath math="C_A = \frac{F_A}{\upsilon} = \frac{F_{A0}(1-X)}{\upsilon_0} = C_{A0}(1-X)" />
                <BlockMath math="C_B = C_{A0}\left(\Theta_B - \frac{b}{a}X\right)" />
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════
              EQUILIBRIUM CONVERSION
          ══════════════════════════════════════════════ */}
          <section className="cre4-section" id="batch-equilibrium">
            <div className="cre4-lec-badge cre4-lec-badge--block3">Building Block 3 · Stoichiometry</div>
            <div className="cre4-section-label">Equilibrium Conversion</div>
            <h2>Batch Reactor Example: Calculate Xₑ</h2>
            <div className="cre4-section-divider"></div>
            <p>
              Consider the elementary reversible reaction:
            </p>
            <div className="cre4-eq-box">
              <div className="cre4-eq-main">
                <BlockMath math="2A \rightleftharpoons B" />
              </div>
              <div className="cre4-eq-note">Given: K_C = 20 dm³/mol, C_A0 = 0.2 mol/dm³</div>
            </div>

            {/* DIAGRAM 8: Equilibrium Stoichiometry Table */}
            <div className="cre4-stoich-table-visual cre4-stoich-equilibrium">
              <div className="cre4-stoich-row cre4-stoich-header">
                <span>Species</span>
                <span>Initial</span>
                <span>Change</span>
                <span>Remaining</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">A</span>
                <span>N<sub>A0</sub></span>
                <span>-N<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">N<sub>A</sub> = N<sub>A0</sub>(1-X)</span>
              </div>
              <div className="cre4-stoich-row">
                <span className="cre4-stoich-species">B</span>
                <span>0</span>
                <span>+½ N<sub>A0</sub>X</span>
                <span className="cre4-stoich-result">N<sub>B</sub> = N<sub>A0</sub>·X/2</span>
              </div>
              <div className="cre4-stoich-row cre4-stoich-total">
                <span>Total</span>
                <span>N<sub>T0</sub> = N<sub>A0</sub></span>
                <span>-N<sub>A0</sub>X/2</span>
                <span className="cre4-stoich-result">N<sub>T</sub> = N<sub>A0</sub> - N<sub>A0</sub>X/2</span>
              </div>
            </div>

            <div className="cre4-steps">
              <div className="cre4-step">
                <div className="cre4-step-num">1</div>
                <div className="cre4-step-content">
                  <h4>Rate Law (Elementary, Reversible)</h4>
                  <div className="cre4-eq-box">
                    <div className="cre4-eq-main">
                      <BlockMath math="-r_A = k_A\left[C_A^2 - \frac{C_B}{K_C}\right]" />
                      <BlockMath math="K_C = \frac{k_A}{k_B}" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="cre4-step">
                <div className="cre4-step-num">2</div>
                <div className="cre4-step-content">
                  <h4>Stoichiometry (Constant Volume Batch)</h4>
                  <div className="cre4-eq-box">
                    <div className="cre4-eq-main">
                      <BlockMath math="C_A = C_{A0}(1-X)" />
                      <BlockMath math="C_B = \frac{C_{A0}X}{2}" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="cre4-step">
                <div className="cre4-step-num">3</div>
                <div className="cre4-step-content">
                  <h4>At Equilibrium: -r_A = 0</h4>
                  <div className="cre4-eq-box">
                    <div className="cre4-eq-label">Equilibrium Condition</div>
                    <div className="cre4-eq-main">
                      <BlockMath math="0 = C_{Ae}^2 - \frac{C_{Be}}{K_C}" />
                      <BlockMath math="K_C = \frac{C_{Be}}{C_{Ae}^2}" />
                    </div>
                  </div>
                  <div className="cre4-eq-box">
                    <div className="cre4-eq-label">Substitute Stoichiometry</div>
                    <div className="cre4-eq-main">
                      <BlockMath math="K_C = \frac{\frac{C_{A0}X_e}{2}}{\left[C_{A0}(1-X_e)\right]^2} = \frac{X_e}{2C_{A0}(1-X_e)^2}" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="cre4-step">
                <div className="cre4-step-num">4</div>
                <div className="cre4-step-content">
                  <h4>Solve for Xₑ</h4>
                  <div className="cre4-eq-box">
                    <div className="cre4-eq-main">
                      <BlockMath math="2K_C C_{A0} = \frac{X_e}{(1-X_e)^2}" />
                      <BlockMath math="2(20)(0.2) = \frac{X_e}{(1-X_e)^2} = 8" />
                      <BlockMath math="\frac{X_e}{(1-X_e)^2} = 8" />
                      <BlockMath math="X_e = \mathbf{0.703}" />
                    </div>
                    <div className="cre4-eq-note">Batch equilibrium conversion = 70.3%</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="cre4-callout cre4-callout--key">
              <p><strong>Note:</strong> For reversible reactions, Xₑ is the maximum achievable conversion. The design equation reaches Xₑ as t → ∞ (batch) or V → ∞ (flow).</p>
            </div>
          </section>

          {/* KEY FORMULAS */}
          <section className="cre4-section" id="key-formulas">
            <div className="cre4-section-label">Reference</div>
            <h2>Key Formulas — Lecture 4</h2>
            <div className="cre4-section-divider"></div>

            <div className="cre4-card-grid">
              <div className="cre4-card">
                <h4>Conversion</h4>
                <BlockMath math="X = \frac{\text{moles A reacted}}{\text{moles A fed}}" />
              </div>
              <div className="cre4-card">
                <h4>Arrhenius</h4>
                <BlockMath math="k = A e^{-E/RT}" />
              </div>
              <div className="cre4-card">
                <h4>δ (mole change)</h4>
                <BlockMath math="\delta = \frac{d}{a}+\frac{c}{a}-\frac{b}{a}-1" />
              </div>
              <div className="cre4-card">
                <h4>Θ (feed ratio)</h4>
                <BlockMath math="\Theta_i = \frac{C_{i0}}{C_{A0}}" />
              </div>
              <div className="cre4-card">
                <h4>Batch C_A</h4>
                <BlockMath math="C_A = C_{A0}(1-X)" />
              </div>
              <div className="cre4-card">
                <h4>Equilibrium K_C</h4>
                <BlockMath math="K_C = \frac{C_C^c C_D^d}{C_A^a C_B^b}" />
              </div>
            </div>
          </section>

          {/* KEY SYMBOLS */}
          <section className="cre4-section" id="key-symbols">
            <div className="cre4-section-label">Reference</div>
            <h2>Key Symbols &amp; Variables — Lecture 4</h2>
            <div className="cre4-section-divider"></div>

            <table className="cre4-table">
              <thead>
                <tr><th>Symbol</th><th>Meaning</th><th>Typical Units</th></tr>
              </thead>
              <tbody>
                <tr><td>X</td><td>Conversion of limiting reactant A</td><td>dimensionless (0 to 1)</td></tr>
                <tr><td>Xₑ</td><td>Equilibrium conversion</td><td>dimensionless</td></tr>
                <tr><td>−r_A</td><td>Rate of disappearance of A per unit volume</td><td>mol/dm³/s</td></tr>
                <tr><td>k</td><td>Specific reaction rate constant</td><td>depends on reaction order</td></tr>
                <tr><td>A</td><td>Frequency factor (Arrhenius)</td><td>same as k</td></tr>
                <tr><td>E</td><td>Activation energy</td><td>cal/mol or J/mol</td></tr>
                <tr><td>R</td><td>Gas constant</td><td>1.987 cal/mol·K</td></tr>
                <tr><td>K_C</td><td>Equilibrium constant (concentration basis)</td><td>(mol/dm³)^(Δn)</td></tr>
                <tr><td>Θ_i</td><td>Initial ratio N_i0/N_A0 or F_i0/F_A0</td><td>dimensionless</td></tr>
                <tr><td>δ</td><td>Change in total moles per mole A reacted</td><td>dimensionless</td></tr>
                <tr><td>N_A0</td><td>Initial moles of A (batch)</td><td>mol</td></tr>
                <tr><td>F_A0</td><td>Inlet molar flow rate of A (flow)</td><td>mol/s or mol/min</td></tr>
                <tr><td>V</td><td>Reactor volume</td><td>dm³ or m³</td></tr>
                <tr><td>W</td><td>Catalyst weight (PBR)</td><td>kg</td></tr>
              </tbody>
            </table>
          </section>

          <div className="cre4-footer">
            Based on: H. Scott Fogler, "Elements of Chemical Reaction Engineering," 6th Edition, University of Michigan · Lecture 4 · Chapters 2-4 · Course notes compiled for educational use
          </div>

        </div>
      </main>
    </div>

        </div>   {/* end scrollable content */}
      </div>     {/* end rightCol */}
    </div>       
  );
};

export default ChemicalReactionEngineeringNotes4;